import React, { useEffect, useMemo, useState } from 'react';
import './OrderDetailsModal.css';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { isBrickFactoryUser } from '../utils/businessType';
import { isFactoryRawMaterialCategory, factoryShowsSellingPrice } from '../constants/factoryStockCategories';

export default function OrderDetailsModal({
  order,
  onClose,
  onApprove,
  onReject,
  onStatusUpdate,
  onReceive,
  onDownloadPo,
  onDownloadProforma,
  onDownloadInvoice,
  isOwner,
  processingId,
  factoryMaterialOrder = false,
  automotivePurchaseOrder = false,
}) {
  const { user } = useAuth();
  const catalogPurchaseOrder = factoryMaterialOrder || automotivePurchaseOrder;
  const factoryMode = isBrickFactoryUser(user) || catalogPurchaseOrder;
  const [statusDraft, setStatusDraft] = useState('ordered');
  const [statusComment, setStatusComment] = useState('');
  const [receiveLines, setReceiveLines] = useState({});
  const [receiveComment, setReceiveComment] = useState('');
  const [shareError, setShareError] = useState('');

  useEffect(() => {
    if (!order) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [order, onClose]);

  const products = useMemo(
    () => order?.product_requests || order?.productRequests || [],
    [order]
  );
  const approvalLogs = order?.approval_logs || order?.approvalLogs || [];
  const busy = order ? processingId === order.id : false;
  const canReceive = isOwner && ['approved', 'ordered', 'partially_received'].includes(order?.status);
  const canTransition = isOwner && ['approved', 'ordered', 'partially_received', 'pending_approval', 'pending'].includes(order?.status);
  const canInvoice = isOwner && ['partially_received', 'received'].includes(order?.status);

  useEffect(() => {
    if (!order) return;
    const lines = {};
    products.forEach((p) => {
      const factoryRaw = factoryMode && isFactoryRawMaterialCategory(p.category);
      const catalogLine = catalogPurchaseOrder || factoryRaw;
      lines[p.id] = {
        qty: '',
        unitCost: catalogLine ? '' : p.actual_unit_cost_tzs ?? p.estimated_unit_cost_tzs ?? '',
        sellingPrice: '',
      };
    });
    setReceiveLines(lines);
    const st = order.status || 'pending_approval';
    if (st === 'approved' || st === 'ordered' || st === 'partially_received') {
      setStatusDraft('ordered');
    } else if (st === 'pending_approval' || st === 'pending') {
      setStatusDraft('approved');
    } else {
      setStatusDraft('cancelled');
    }
    setStatusComment('');
    setReceiveComment('');
    setShareError('');
  }, [order, products, factoryMode, catalogPurchaseOrder]);

  const receiveRows = useMemo(
    () =>
      products.map((p) => ({
        ...p,
        remainingQty: Math.max(0, (Number(p.quantity) || 0) - (Number(p.received_quantity) || 0)),
      })),
    [products]
  );

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  const submittedBy = order?.user?.name || order?.user?.email || order?.user_id || '—';
  const supplierName = order?.supplier_name || order?.supplier?.name || '—';
  const supplierPhone = order?.supplier_phone || order?.supplier?.phone || '—';
  const priority = order?.priority || 'normal';
  const neededBy = order?.needed_by_date || '—';
  const paymentTerms = order?.payment_terms || '—';
  const paymentDueDate = order?.payment_due_date || '—';
  const estimatedTotal = order?.estimated_total_tzs != null
    ? Math.round(Number(order?.estimated_total_tzs) || 0).toLocaleString()
    : '0';
  const statusLabel = (order?.status || 'pending_approval').replace(/_/g, ' ');
  const handleReceiveField = (id, field, value) => {
    setReceiveLines((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const submitReceive = () => {
    const lines = receiveRows
      .map((row) => {
        const line = receiveLines[row.id] || {};
        const qty = Number.parseInt(String(line.qty || 0), 10);
        const factoryRaw = factoryMode && isFactoryRawMaterialCategory(row.category);
        const catalogLine = catalogPurchaseOrder || factoryRaw;
        const payload = {
          product_request_id: row.id,
          received_qty: Number.isFinite(qty) ? Math.max(0, qty) : 0,
        };
        if (catalogLine) {
          if (line.sellingPrice !== '' && line.sellingPrice != null) {
            payload.selling_price_tzs = Number(line.sellingPrice);
          }
        } else {
          if (line.unitCost !== '' && line.unitCost != null) {
            payload.actual_unit_cost_tzs = Number(line.unitCost);
          }
          if (factoryMode && line.sellingPrice !== '' && line.sellingPrice != null) {
            payload.selling_price_tzs = Number(line.sellingPrice);
          }
        }
        return payload;
      })
      .filter((line) => line.received_qty > 0);

    if (lines.length === 0) return;
    void onReceive(order.id, { lines, comment: receiveComment || undefined });
  };

  const whatsappDigits = (raw) => {
    const d = String(raw || '').replace(/\D/g, '');
    if (!d) return '';
    if (d.startsWith('255')) return d;
    if (d.startsWith('0') && d.length === 10) return `255${d.slice(1)}`;
    if (d.startsWith('7') && d.length === 9) return `255${d}`;
    return d;
  };

  const openWhatsapp = (docType) => {
    const phone = whatsappDigits(order?.supplier_phone || order?.supplier?.phone || '');
    if (!phone) {
      setShareError('Add supplier phone number first to send via WhatsApp.');
      return;
    }
    setShareError('');

    const poNumber = order?.po_number || `PO-${order?.id}`;
    const supplier = order?.supplier_name || order?.supplier?.name || 'Supplier';
    const totalEstimated = `TZS ${Math.round(Number(order?.estimated_total_tzs || 0)).toLocaleString()}`;
    const totalInvoiced = `TZS ${Math.round(
      (products || []).reduce((sum, p) => sum + Number(p.received_line_total_tzs || 0), 0)
    ).toLocaleString()}`;
    const baseLines = [
      `Hello ${supplier},`,
      '',
      `Please find ${docType} for ${poNumber}.`,
      `Status: ${(order?.status || '').replace(/_/g, ' ') || 'pending approval'}`,
      `Estimated total: ${totalEstimated}`,
    ];

    if (docType === 'Invoice') {
      baseLines.push(`Invoice total (received): ${totalInvoiced}`);
    }

    baseLines.push('', 'Kindly confirm receipt. Thank you.');

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(baseLines.join('\n'))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!order) return null;

  return (
    <div className="orders-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="orders-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="orders-modal-title"
      >
        <button type="button" className="orders-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        <h2 id="orders-modal-title" className="orders-modal-title">
          {factoryMode ? 'Material order details' : 'Purchase order details'}
        </h2>

        {isOwner ? (
          <>
            <div className="orders-modal-top-actions">
              <button
                type="button"
                className="orders-action-btn orders-action-btn--ghost"
                onClick={() => void onDownloadPo(order)}
              >
                {factoryMode ? 'Download order PDF' : 'Download PO PDF'}
              </button>
              <button
                type="button"
                className="orders-action-btn orders-action-btn--ghost"
                onClick={() => openWhatsapp('PO')}
              >
                {factoryMode ? 'Send order WhatsApp' : 'Send PO WhatsApp'}
              </button>
              {!factoryMode ? (
                <>
                  <button
                    type="button"
                    className="orders-action-btn orders-action-btn--ghost"
                    onClick={() => void onDownloadProforma(order)}
                  >
                    Generate Proforma
                  </button>
                  <button
                    type="button"
                    className="orders-action-btn orders-action-btn--ghost"
                    onClick={() => openWhatsapp('Proforma')}
                  >
                    Send Proforma WhatsApp
                  </button>
                  <button
                    type="button"
                    className="orders-action-btn orders-action-btn--ghost"
                    onClick={() => void onDownloadInvoice(order)}
                    disabled={!canInvoice}
                    title={canInvoice ? 'Generate invoice from received quantities' : 'Invoice available after receiving stock'}
                  >
                    Generate Invoice
                  </button>
                  <button
                    type="button"
                    className="orders-action-btn orders-action-btn--ghost"
                    onClick={() => openWhatsapp('Invoice')}
                    disabled={!canInvoice}
                    title={canInvoice ? 'Open supplier chat with invoice message' : 'Invoice message available after receiving stock'}
                  >
                    Send Invoice WhatsApp
                  </button>
                </>
              ) : null}
            </div>
            {shareError ? <div className="orders-share-error">{shareError}</div> : null}
          </>
        ) : (
          <div className="orders-staff-note">
            {factoryMode
              ? 'Order download/send actions are owner-only.'
              : 'PO, Proforma, Invoice download/send actions are owner-only.'}
          </div>
        )}

        <div className="orders-modal-status-row">
          <span className="orders-modal-label">Status</span>
          <span className={`orders-status-badge orders-status-badge--readonly ${order.status || 'pending_approval'}`}>
            {(order.status === 'approved' || order.status === 'received') && <FaCheckCircle aria-hidden />}
            {order.status === 'rejected' && <FaTimesCircle aria-hidden />}
            {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
          </span>
        </div>

        <dl className="orders-modal-meta">
          <div className="orders-modal-meta-row">
            <dt>Order ID</dt>
            <dd>{order.po_number || order.id}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Requested by</dt>
            <dd>{submittedBy}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Submitted</dt>
            <dd>{formatDate(order.created_at)}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Supplier</dt>
            <dd>{supplierName}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Supplier phone</dt>
            <dd>{supplierPhone}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Priority</dt>
            <dd style={{ textTransform: 'capitalize' }}>{priority}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Needed by</dt>
            <dd>{neededBy}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Payment terms</dt>
            <dd>{paymentTerms}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Payment due</dt>
            <dd>{paymentDueDate}</dd>
          </div>
          <div className="orders-modal-meta-row">
            <dt>Est. order total</dt>
            <dd>TZS {estimatedTotal}</dd>
          </div>
        </dl>

        {order.notes ? (
          <div className="orders-modal-notes">
            <span className="orders-modal-label">Notes</span>
            <p className="orders-modal-notes-body">{order.notes}</p>
          </div>
        ) : null}

        <div className="orders-modal-products">
          <span className="orders-modal-label">Line items</span>
          <ul className="orders-modal-lines">
            {products.length === 0 ? (
              <li className="orders-modal-line orders-modal-line--empty">No line items</li>
            ) : (
              products.map((p, idx) => (
                <li key={p.id ?? idx} className="orders-modal-line">
                  <div className="orders-modal-line-main">
                    <strong>{p.product_name}</strong>
                    <span className="orders-modal-line-qty">×{p.quantity}</span>
                  </div>
                  {p.category ? (
                    <div className="orders-modal-line-meta">Category: {p.category}</div>
                  ) : null}
                  <div className="orders-modal-line-meta">
                    Unit cost: TZS {Math.round(Number(p.estimated_unit_cost_tzs) || 0).toLocaleString()} | Line total:
                    TZS {Math.round(Number(p.estimated_line_total_tzs) || 0).toLocaleString()}
                  </div>
                  <div className="orders-modal-line-meta">
                    Received: {Number(p.received_quantity || 0)} / {Number(p.quantity || 0)}
                    {p.actual_unit_cost_tzs != null
                      ? ` | Actual cost: TZS ${Math.round(Number(p.actual_unit_cost_tzs) || 0).toLocaleString()}`
                      : ''}
                  </div>
                  {p.reason ? <div className="orders-modal-line-reason">{p.reason}</div> : null}
                </li>
              ))
            )}
          </ul>
        </div>

        {isOwner && (order.status === 'pending_approval' || order.status === 'pending') && (
          <div className="orders-modal-actions-wrap">
            <p className="orders-modal-actions-hint">
              Review supplier details, line items, and notes above, then choose <strong>Approve</strong> or <strong>Reject</strong>.
            </p>
            <div className="orders-modal-actions">
              <button
                type="button"
                className="orders-action-btn approve"
                disabled={busy}
                onClick={() => void onApprove(order.id)}
              >
                <FaCheckCircle /> Approve
              </button>
              <button
                type="button"
                className="orders-action-btn reject"
                disabled={busy}
                onClick={() => void onReject(order.id)}
              >
                <FaTimesCircle /> Reject
              </button>
            </div>
          </div>
        )}

        {canTransition && (
          <div className="orders-modal-actions-wrap">
            <p className="orders-modal-actions-hint">
              Workflow update (example: Approved → Ordered, or cancel if supplier cannot fulfill).
            </p>
            <div className="orders-modal-transition-row">
              <select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)} disabled={busy}>
                {order.status === 'approved' && <option value="ordered">Mark as ordered</option>}
                {order.status === 'partially_received' && <option value="ordered">Keep as ordered</option>}
                {(order.status === 'pending_approval' || order.status === 'pending') && (
                  <option value="approved">Approve (quick)</option>
                )}
                <option value="cancelled">Cancel PO</option>
              </select>
              <input
                type="text"
                placeholder="Status comment (optional)"
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                disabled={busy}
              />
              <button
                type="button"
                className="orders-action-btn orders-action-btn--ghost"
                disabled={busy}
                onClick={() => void onStatusUpdate(order.id, statusDraft, statusComment)}
              >
                Update status
              </button>
            </div>
          </div>
        )}

        {canReceive && (
          <div className="orders-modal-actions-wrap">
            <p className="orders-modal-actions-hint">
              {catalogPurchaseOrder
                ? 'Enter quantity received and selling price (TZS). Purchase price comes from the order. Stock updates immediately.'
                : 'Receive stock directly against this PO. Any received quantity updates inventory immediately.'}
            </p>
            <div className="orders-receive-grid">
              {receiveRows.map((row) => {
                const factoryRaw = factoryMode && isFactoryRawMaterialCategory(row.category);
                const catalogLine = catalogPurchaseOrder || factoryRaw;
                const showSellingOnReceive =
                  catalogPurchaseOrder ||
                  (factoryMode && (factoryRaw || factoryShowsSellingPrice(row.category)));
                const orderPurchase = Math.round(Number(row.estimated_unit_cost_tzs) || 0);
                return (
                  <div key={row.id} className="orders-receive-line">
                    <div className="orders-receive-title">
                      {row.product_name} <span>Remaining: {row.remainingQty}</span>
                    </div>
                    {catalogLine ? (
                      <div className="orders-receive-meta">
                        Purchase price (from order): TZS {orderPurchase.toLocaleString()}
                      </div>
                    ) : null}
                    <input
                      type="number"
                      min="0"
                      max={row.remainingQty}
                      placeholder="Qty to receive now"
                      value={receiveLines[row.id]?.qty ?? ''}
                      onChange={(e) => handleReceiveField(row.id, 'qty', e.target.value)}
                      disabled={busy || row.remainingQty <= 0}
                    />
                    {!catalogLine ? (
                      <input
                        type="number"
                        min="0"
                        placeholder="Actual unit cost"
                        value={receiveLines[row.id]?.unitCost ?? ''}
                        onChange={(e) => handleReceiveField(row.id, 'unitCost', e.target.value)}
                        disabled={busy || row.remainingQty <= 0}
                      />
                    ) : null}
                    {showSellingOnReceive ? (
                      <input
                        type="number"
                        min="0"
                        placeholder="Selling price (TZS)"
                        value={receiveLines[row.id]?.sellingPrice ?? ''}
                        onChange={(e) => handleReceiveField(row.id, 'sellingPrice', e.target.value)}
                        disabled={busy || row.remainingQty <= 0}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
            <input
              type="text"
              placeholder="Receiving note (optional)"
              value={receiveComment}
              onChange={(e) => setReceiveComment(e.target.value)}
              disabled={busy}
              className="orders-receive-comment"
            />
            <button type="button" className="orders-action-btn approve" disabled={busy} onClick={submitReceive}>
              Receive selected lines
            </button>
          </div>
        )}

        {approvalLogs.length > 0 && (
          <div className="orders-modal-actions-wrap">
            <span className="orders-modal-label">Approval timeline</span>
            <ul className="orders-approval-timeline">
              {approvalLogs.map((log) => (
                <li key={log.id}>
                  <strong>{(log.event || '').replace(/_/g, ' ')}</strong>
                  <span>
                    {' '}
                    {log.from_status ? `${log.from_status} → ` : ''}
                    {log.to_status || ''}
                  </span>
                  <div>
                    {(log.actor?.name || log.actor?.email || 'System')} • {formatDate(log.created_at)}
                  </div>
                  {log.comment ? <p>{log.comment}</p> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
